import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LoadingContainer } from '../components/ui/Spinner';
import { PlayIcon, FlagIcon, PlusIcon, XMarkIcon, PencilIcon, TrashIcon, CameraIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import PlayerTypeIcon from '../components/PlayerTypeIcon';
import BreakEnforcement from '../components/BreakEnforcement';
import ChatPanel from '../components/ChatPanel';

function ActiveSessionPage() {
    const navigate = useNavigate();
    const [activeSession, setActiveSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [elapsedTime, setElapsedTime] = useState('0:00:00');
    const [showStartModal, setShowStartModal] = useState(false);
    const [showReadyCheck, setShowReadyCheck] = useState(false);
    const [checkInState, setCheckInState] = useState({
        hungerRisk: false,        // Yes = risk present
        sleepRisk: false,
        energyRisk: false,
        frustrationRisk: false,
        overconfidenceRisk: false,
        notificationsOff: true,
    });
    const [stopLossAmount, setStopLossAmount] = useState('2000');
    const [pendingStartForm, setPendingStartForm] = useState(null);
    const [showEndModal, setShowEndModal] = useState(false);
    const [showBuyInModal, setShowBuyInModal] = useState(false);
    const [showMistakeModal, setShowMistakeModal] = useState(false);
    const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
    const [showCheckpointModal, setShowCheckpointModal] = useState(false);
    const [showBigFoldModal, setShowBigFoldModal] = useState(false);
    const [showCuriosityCallModal, setShowCuriosityCallModal] = useState(false);
    const [showRakebackModal, setShowRakebackModal] = useState(false);
    const [showChatPanel, setShowChatPanel] = useState(false);
    const [locations, setLocations] = useState([]);
    const [stakes, setStakes] = useState([]);
    const [mistakeTypes, setMistakeTypes] = useState([]);
    const [allPlayers, setAllPlayers] = useState([]);
    const [sessionPlayers, setSessionPlayers] = useState([]);
    // Start session form
    const [startForm, setStartForm] = useState({
        location_id: '',
        stake_id: '',
        game_type: 'NLHE',
        buy_in: '1000',
        notes: '',
    });

    // Buy-in form
    const [buyInForm, setBuyInForm] = useState({
        amount: '',
        notes: '',
    });

    // End session form
    const [endSessionForm, setEndSessionForm] = useState({
        cash_out: '',
        technical_grade: '',
        mental_grade: '',
    });

    // Mistake form
    const [mistakeForm, setMistakeForm] = useState({
        mistake_type_id: '',
        leak_ids: [],
        description: '',
        cost_estimate: '',
        is_curiosity_call: false,
    });
    const [editingMistakeId, setEditingMistakeId] = useState(null);
    const [deleteMistakeId, setDeleteMistakeId] = useState(null);
    const [leaks, setLeaks] = useState([]);
    const [editingCheckpointId, setEditingCheckpointId] = useState(null);
    const [deleteCheckpointId, setDeleteCheckpointId] = useState(null);
    const [breakConfig, setBreakConfig] = useState(null);

    // Checkpoint form
    const [checkpointForm, setCheckpointForm] = useState({
        stack_size: '',
        pnl: '',
        notes: '',
    });

    // Big fold form
    const [bigFoldForm, setBigFoldForm] = useState({
        hand_log: '',
        call_amount: '',
        description: '',
        difficulty: 'easy',
    });

    // Curiosity call form
    const [curiosityCallForm, setCuriosityCallForm] = useState({
        hand_log: '',
        call_amount: '',
        description: '',
        severity: 'pretty_bad',
    });

    // Rakeback form
    const [rakebackForm, setRakebackForm] = useState({
        amount: '',
    });

    // Simple hand log
    const [showHandLogModal, setShowHandLogModal] = useState(false);
    const [handLogForm, setHandLogForm] = useState({
        description: '',
        amount: '',
        outcome: 'won',
        players_involved_ids: [],
        tags: [],
    });
    const [handTags, setHandTags] = useState([]);
    const [newTagName, setNewTagName] = useState('');
    const [showAdvancedLogs, setShowAdvancedLogs] = useState(false);

    // Quick Calling Log (discipline folds / curiosity calls)
    const [showQuickLogModal, setShowQuickLogModal] = useState(false);
    const [quickLogType, setQuickLogType] = useState('fold'); // 'fold' or 'call'
    const [quickLogForm, setQuickLogForm] = useState({
        villain_id: '',
        hole_cards: '',
        amount: '',
    });
    const [quickLogSubmitting, setQuickLogSubmitting] = useState(false);
    const [quickLogToast, setQuickLogToast] = useState(null);

    // Session notes
    const [newNoteContent, setNewNoteContent] = useState('');
    const [addingNote, setAddingNote] = useState(false);

    // Add player forms
    const [seatPosition, setSeatPosition] = useState('');
    const [newPlayerForm, setNewPlayerForm] = useState({
        name: '',
        description: '',
        player_category: 'unknown',
        profile_picture: null,
    });
    const [picturePreview, setPicturePreview] = useState(null);
    const [locationPlayers, setLocationPlayers] = useState([]);
    const [showAllLocations, setShowAllLocations] = useState(false);
    const [showNewPlayerForm, setShowNewPlayerForm] = useState(false);
    const [loadingPlayers, setLoadingPlayers] = useState(false);
    const [playerSearchQuery, setPlayerSearchQuery] = useState('');
    const [recentPlayers, setRecentPlayers] = useState([]);
    const [searchResults, setSearchResults] = useState([]);

    const fetchActiveSession = useCallback(async () => {
        try {
            // First get the list of active sessions to find the ID
            const response = await axios.get('/api/sessions/', {
                params: { status: 'active' }
            });
            const sessions = response.data?.results || response.data || [];

            if (sessions.length > 0) {
                const sessionId = sessions[0].id;
                // Then fetch the full session details with nested objects (mistakes, curiosity_calls, big_folds, etc.)
                const detailResponse = await axios.get(`/api/sessions/${sessionId}/`);
                setActiveSession(detailResponse.data);
            } else {
                setActiveSession(null);
            }
        } catch (err) {
            console.error('Failed to fetch active session:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Refresh current session directly by ID (faster, use after updates)
    const refreshSession = useCallback(async () => {
        if (!activeSession?.id) return;
        try {
            const response = await axios.get(`/api/sessions/${activeSession.id}/`);
            setActiveSession(response.data);
        } catch (err) {
            console.error('Failed to refresh session:', err);
        }
    }, [activeSession?.id]);

    const fetchLocations = async () => {
        try {
            const response = await axios.get('/api/locations/');
            setLocations(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch locations:', err);
        }
    };

    const fetchStakes = async () => {
        try {
            const response = await axios.get('/api/stakes/');
            setStakes(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch stakes:', err);
        }
    };

    const fetchMistakeTypes = async () => {
        try {
            const response = await axios.get('/api/mistake-types/');
            const types = response.data?.results || response.data || [];
            setMistakeTypes(types);
            // Set first type as default if available
            if (types.length > 0 && !mistakeForm.mistake_type_id) {
                setMistakeForm(prev => ({...prev, mistake_type_id: types[0].id}));
            }
        } catch (err) {
            console.error('Failed to fetch mistake types:', err);
        }
    };

    const fetchLeaks = async () => {
        try {
            const response = await axios.get('/api/leaks/');
            setLeaks(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch leaks:', err);
        }
    };

    const fetchHandTags = async () => {
        try {
            const response = await axios.get('/api/hand-tags/');
            setHandTags(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch hand tags:', err);
        }
    };

    const createHandTag = async (name) => {
        try {
            const response = await axios.post('/api/hand-tags/get_or_create/', { name, color: 'blue' });
            if (response.data.created) {
                setHandTags(prev => [...prev, response.data]);
            }
            return response.data;
        } catch (err) {
            console.error('Failed to create hand tag:', err);
            return null;
        }
    };

    const fetchAllPlayers = async () => {
        setLoadingPlayers(true);
        try {
            const response = await axios.get('/api/players/');
            setAllPlayers(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch players:', err);
        } finally {
            setLoadingPlayers(false);
        }
    };

    const fetchLocationPlayers = useCallback(async (locationId) => {
        if (!locationId) {
            setLocationPlayers([]);
            return;
        }
        setLoadingPlayers(true);
        try {
            const response = await axios.get('/api/players/', {
                params: { location: locationId, sort: 'sessions' }
            });
            setLocationPlayers(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch location players:', err);
            setLocationPlayers([]);
        } finally {
            setLoadingPlayers(false);
        }
    }, []);

    // Fetch players using at-table endpoint (recent + optional search)
    const fetchAtTablePlayers = useCallback(async (locationId, query = '') => {
        setLoadingPlayers(true);
        try {
            const params = { limit: 15 };
            if (locationId) params.location = locationId;
            if (query.trim()) params.q = query.trim();

            const response = await axios.get('/api/players/at-table/', { params });
            setRecentPlayers(response.data?.recent || []);
            setSearchResults(response.data?.search_results || []);
        } catch (err) {
            console.error('Failed to fetch at-table players:', err);
            setRecentPlayers([]);
            setSearchResults([]);
        } finally {
            setLoadingPlayers(false);
        }
    }, []);

    // Debounced search for player lookup
    const searchTimeoutRef = useRef(null);
    const handlePlayerSearch = useCallback((query) => {
        setPlayerSearchQuery(query);

        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Debounce search by 200ms
        searchTimeoutRef.current = setTimeout(() => {
            fetchAtTablePlayers(activeSession?.location?.id, query);
        }, 200);
    }, [activeSession?.location?.id, fetchAtTablePlayers]);

    const fetchSessionPlayers = useCallback(async () => {
        if (!activeSession) return;
        try {
            const response = await axios.get('/api/session-players/', {
                params: { session: activeSession.id }
            });
            setSessionPlayers(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch session players:', err);
        }
    }, [activeSession]);

    const fetchBreakConfig = async () => {
        try {
            const response = await axios.get('/api/break-config/');
            const configs = response.data?.results || response.data || [];
            if (configs.length > 0) {
                setBreakConfig(configs[0]);
            } else if (configs.id) {
                setBreakConfig(configs);
            }
        } catch (err) {
            console.error('Failed to fetch break config:', err);
        }
    };

    useEffect(() => {
        fetchActiveSession();
        fetchLocations();
        fetchStakes();
        fetchMistakeTypes();
        fetchLeaks();
        fetchAllPlayers();
        fetchHandTags();
        fetchBreakConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeSession) {
            fetchSessionPlayers();
        }
    }, [activeSession, fetchSessionPlayers]);

    // Fetch players when modal opens - use at-table endpoint for quick access
    useEffect(() => {
        if (showAddPlayerModal) {
            setPlayerSearchQuery('');
            setSearchResults([]);
            fetchAtTablePlayers(activeSession?.location?.id, '');
        }
    }, [showAddPlayerModal, activeSession?.location?.id, fetchAtTablePlayers]);

    // Update elapsed time every second
    useEffect(() => {
        if (activeSession && activeSession.status === 'active') {
            const interval = setInterval(() => {
                const start = new Date(activeSession.start_time);
                const now = new Date();
                const diff = now - start;
                const hours = Math.floor(diff / 3600000);
                const minutes = Math.floor((diff % 3600000) / 60000);
                const seconds = Math.floor((diff % 60000) / 1000);
                setElapsedTime(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [activeSession]);

    const handleStartSession = async (e) => {
        e.preventDefault();
        // Save form data and show ready check instead of starting immediately
        setPendingStartForm({...startForm});
        setShowStartModal(false);
        setShowReadyCheck(true);
        // Reset check-in state to defaults
        setCheckInState({
            hungerRisk: false,
            sleepRisk: false,
            energyRisk: false,
            frustrationRisk: false,
            overconfidenceRisk: false,
            notificationsOff: true,
        });
        setStopLossAmount('2000');
    };

    const handleConfirmReady = async () => {
        if (!pendingStartForm) return;
        try {
            // Infer game_type from stake name
            const selectedStake = stakes.find(s => s.id === parseInt(pendingStartForm.stake_id));
            const inferredGameType = selectedStake?.name?.toUpperCase().includes('PLO') ? 'PLO' : 'NLHE';

            const sessionData = {
                ...pendingStartForm,
                game_type: inferredGameType,
                status: 'active',
                start_time: new Date().toISOString(),
                current_buy_in: parseFloat(pendingStartForm.buy_in),
                cash_out: 0,
            };
            // Add stop_loss if a value was entered
            if (stopLossAmount && parseFloat(stopLossAmount) > 0) {
                sessionData.stop_loss = parseFloat(stopLossAmount);
            }
            const response = await axios.post('/api/sessions/', sessionData);

            // Create initial buy-in record
            await axios.post('/api/session-buyins/', {
                session: response.data.id,
                amount: parseFloat(pendingStartForm.buy_in),
                notes: 'Initial buy-in',
            });

            // Create check-in record
            await axios.post('/api/session-checkins/', {
                session: response.data.id,
                hunger_risk: checkInState.hungerRisk,
                sleep_risk: checkInState.sleepRisk,
                energy_risk: checkInState.energyRisk,
                frustration_risk: checkInState.frustrationRisk,
                overconfidence_risk: checkInState.overconfidenceRisk,
                notifications_off: checkInState.notificationsOff,
            });

            // Reload the page to show the new active session
            window.location.reload();
        } catch (err) {
            console.error('Failed to start session:', err);
        }
    };

    const handleCancelReadyCheck = () => {
        setShowReadyCheck(false);
        setPendingStartForm(null);
        setShowStartModal(true); // Go back to start form
    };

    // Check-in is ready when all scales are set (1-5) and stop loss is entered
    const allReadyItemsChecked = checkInState.notificationsOff && stopLossAmount && parseFloat(stopLossAmount) > 0;

    const handleStopSession = () => {
        if (!activeSession) return;
        // Set initial cash_out value to current buy-in if available
        setEndSessionForm({
            cash_out: activeSession.current_buy_in || '',
            technical_grade: '',
            mental_grade: '',
        });
        setShowEndModal(true);
    };

    const handleEndSession = async (e) => {
        e.preventDefault();
        try {
            // First, mark all still-playing players as left
            const stillPlayingPlayers = sessionPlayers.filter(p => p.is_still_playing);
            const endTime = new Date().toISOString();

            await Promise.all(
                stillPlayingPlayers.map(sp =>
                    axios.patch(`/api/session-players/${sp.id}/`, { left_at: endTime })
                )
            );

            // Then end the session
            await axios.patch(`/api/sessions/${activeSession.id}/`, {
                status: 'completed',
                end_time: endTime,
                cash_out: parseFloat(endSessionForm.cash_out),
                technical_grade: endSessionForm.technical_grade || null,
                mental_grade: endSessionForm.mental_grade || null,
            });

            // Clear the active session state
            setActiveSession(null);

            // Dispatch event to notify ActiveSessionAlert to refresh
            window.dispatchEvent(new CustomEvent('session-ended'));

            // Refetch to check if there are any other active sessions
            await refreshSession();

            // Close modal and reset form
            setShowEndModal(false);
            setEndSessionForm({ cash_out: '', technical_grade: '', mental_grade: '' });

            // Navigate to sessions page
            navigate('/sessions');
        } catch (err) {
            console.error('Failed to stop session:', err);
        }
    };

    const handleAddBuyIn = async (e) => {
        e.preventDefault();
        try {
            // Create the buy-in record - backend will automatically update current_buy_in via signal
            await axios.post('/api/session-buyins/', {
                session: activeSession.id,
                amount: parseFloat(buyInForm.amount),
                notes: buyInForm.notes,
            });

            // Fetch updated session to get the recalculated current_buy_in
            await refreshSession();

            setShowBuyInModal(false);
            setBuyInForm({ amount: '', notes: '' });
        } catch (err) {
            console.error('Failed to add buy-in:', err);
        }
    };

    const handleUpdateRakeback = async (e) => {
        e.preventDefault();
        try {
            // Patch the session
            await axios.patch(`/api/sessions/${activeSession.id}/`, {
                rakeback: parseFloat(rakebackForm.amount),
            });

            // Refresh session data
            await refreshSession();

            setShowRakebackModal(false);
            setRakebackForm({ amount: '' });
        } catch (err) {
            console.error('Failed to update rakeback:', err);
        }
    };

    const handleAddMistake = async (e) => {
        e.preventDefault();
        try {
            // Get mistake type name for notes
            const mistakeType = mistakeTypes.find(mt => mt.id === parseInt(mistakeForm.mistake_type_id));
            const mistakeTypeName = mistakeType ? mistakeType.name : '';
            const notes = mistakeTypeName ? `Mistake Type: ${mistakeTypeName}` : '';

            await axios.post('/api/big-hands/', {
                session: activeSession.id,
                hand_description: mistakeForm.description,
                hand_type: mistakeForm.is_curiosity_call ? 'call' : 'spot',
                amount: mistakeForm.cost_estimate ? parseFloat(mistakeForm.cost_estimate) : null,
                notes: notes,
                decision_quality: 'mistake',
                severity: 'moderate',
                leaks_ids: mistakeForm.leak_ids,
            });

            await refreshSession();
            setShowMistakeModal(false);
            // Reset form with first mistake type
            const firstTypeId = mistakeTypes.length > 0 ? mistakeTypes[0].id : '';
            setMistakeForm({ mistake_type_id: firstTypeId, leak_ids: [], description: '', cost_estimate: '', is_curiosity_call: false });
        } catch (err) {
            console.error('Failed to add mistake:', err);
            console.error('Error response:', err.response?.data);
        }
    };

    const handleEditMistake = (mistake) => {
        setEditingMistakeId(mistake.id);
        setMistakeForm({
            mistake_type_id: mistake.mistake_type?.id || '',
            leak_ids: mistake.leaks ? mistake.leaks.map(leak => leak.id) : [],
            description: mistake.description,
            cost_estimate: mistake.cost_estimate || '',
        });
    };

    const handleSaveMistake = async (mistakeId) => {
        try {
            await axios.patch(`/api/session-mistakes/${mistakeId}/`, {
                mistake_type_id: mistakeForm.mistake_type_id,
                leak_ids: mistakeForm.leak_ids,
                description: mistakeForm.description,
                cost_estimate: mistakeForm.cost_estimate ? parseFloat(mistakeForm.cost_estimate) : null,
            });
            await refreshSession();
            setEditingMistakeId(null);
            // Reset form with first mistake type
            const firstTypeId = mistakeTypes.length > 0 ? mistakeTypes[0].id : '';
            setMistakeForm({ mistake_type_id: firstTypeId, leak_ids: [], description: '', cost_estimate: '' });
        } catch (err) {
            console.error('Failed to update mistake:', err);
        }
    };

    const handleCancelEditMistake = () => {
        setEditingMistakeId(null);
        const firstTypeId = mistakeTypes.length > 0 ? mistakeTypes[0].id : '';
        setMistakeForm({ mistake_type_id: firstTypeId, leak_ids: [], description: '', cost_estimate: '' });
    };

    const handleDeleteMistake = async () => {
        if (!deleteMistakeId) return;
        try {
            await axios.delete(`/api/session-mistakes/${deleteMistakeId}/`);
            await refreshSession();
            setDeleteMistakeId(null);
        } catch (err) {
            console.error('Failed to delete mistake:', err);
            setDeleteMistakeId(null);
        }
    };

    const handleCreateAndAddPlayer = async (e) => {
        e.preventDefault();

        try {
            let playerResponse;

            // Use FormData if there's a picture, otherwise use JSON
            if (newPlayerForm.profile_picture) {
                const formData = new FormData();
                formData.append('player_category', newPlayerForm.player_category);
                if (newPlayerForm.name?.trim()) {
                    formData.append('name', newPlayerForm.name.trim());
                }
                if (newPlayerForm.description?.trim()) {
                    formData.append('description', newPlayerForm.description.trim());
                }
                formData.append('profile_picture', newPlayerForm.profile_picture);

                playerResponse = await axios.post('/api/players/', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                // Build player data - name can be empty for anonymous players
                const playerData = {
                    player_category: newPlayerForm.player_category,
                };

                // Add name if provided
                if (newPlayerForm.name?.trim()) {
                    playerData.name = newPlayerForm.name.trim();
                }

                // Add description if provided
                if (newPlayerForm.description?.trim()) {
                    playerData.description = newPlayerForm.description.trim();
                }

                playerResponse = await axios.post('/api/players/', playerData);
            }

            // Then add them to the session
            await axios.post('/api/session-players/', {
                session: activeSession.id,
                player_id: playerResponse.data.id,
                seat_position: seatPosition ? parseInt(seatPosition) : null,
            });

            // Close modal immediately after successful creation
            setShowAddPlayerModal(false);
            setShowNewPlayerForm(false);
            setNewPlayerForm({ name: '', description: '', player_category: 'unknown', profile_picture: null });
            setPicturePreview(null);
            setSeatPosition('');

            // Refresh all data
            await refreshSession();
            fetchAllPlayers();
            fetchSessionPlayers();
            if (activeSession?.location?.id) {
                fetchLocationPlayers(activeSession.location.id);
            }
        } catch (err) {
            console.error('Failed to create and add player:', err);
            const errorMsg = err.response?.data?.detail
                || err.response?.data?.name?.[0]
                || err.response?.data?.non_field_errors?.[0]
                || (err.response?.data ? JSON.stringify(err.response.data) : err.message)
                || 'Failed to create player';
            alert(`Error: ${errorMsg}`);
        }
    };

    const handleRemovePlayer = async (sessionPlayerId) => {
        try {
            await axios.delete(`/api/session-players/${sessionPlayerId}/`);
            await fetchSessionPlayers();
        } catch (err) {
            console.error('Failed to remove player:', err);
        }
    };

    const handleMarkPlayerLeft = async (sessionPlayerId) => {
        try {
            await axios.patch(`/api/session-players/${sessionPlayerId}/`, {
                left_at: new Date().toISOString()
            });
            await fetchSessionPlayers();
        } catch (err) {
            console.error('Failed to mark player as left:', err);
        }
    };

    const handleBringPlayerBack = async (sessionPlayerId) => {
        try {
            await axios.patch(`/api/session-players/${sessionPlayerId}/`, {
                left_at: null
            });
            await fetchSessionPlayers();
        } catch (err) {
            console.error('Failed to bring player back:', err);
        }
    };

    const handleAddCheckpoint = async (e) => {
        e.preventDefault();
        try {
            if (editingCheckpointId) {
                // Update existing checkpoint
                await axios.patch(`/api/session-checkpoints/${editingCheckpointId}/`, {
                    stack_size: parseFloat(checkpointForm.stack_size),
                    pnl: parseFloat(checkpointForm.pnl),
                    notes: checkpointForm.notes,
                });
            } else {
                // Create new checkpoint
                await axios.post('/api/session-checkpoints/', {
                    session: activeSession.id,
                    stack_size: parseFloat(checkpointForm.stack_size),
                    pnl: parseFloat(checkpointForm.pnl),
                    notes: checkpointForm.notes,
                });
            }

            await refreshSession();
            setShowCheckpointModal(false);
            setCheckpointForm({ stack_size: '', pnl: '', notes: '' });
            setEditingCheckpointId(null);
        } catch (err) {
            console.error('Failed to save checkpoint:', err);
        }
    };

    const handleEditCheckpoint = (checkpoint) => {
        setEditingCheckpointId(checkpoint.id);
        setCheckpointForm({
            stack_size: checkpoint.stack_size || '',
            pnl: checkpoint.pnl || '',
            notes: checkpoint.notes || '',
        });
        setShowCheckpointModal(true);
    };

    const handleDeleteCheckpoint = async () => {
        try {
            await axios.delete(`/api/session-checkpoints/${deleteCheckpointId}/`);
            await refreshSession();
            setDeleteCheckpointId(null);
        } catch (err) {
            console.error('Failed to delete checkpoint:', err);
        }
    };

    const handleCancelCheckpoint = () => {
        setShowCheckpointModal(false);
        setCheckpointForm({ stack_size: '', pnl: '', notes: '' });
        setEditingCheckpointId(null);
    };

    const handleAddBigFold = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/api/big-hands/', {
                session: activeSession.id,
                hand_description: bigFoldForm.hand_log,
                hand_type: 'fold',
                amount: bigFoldForm.call_amount ? parseFloat(bigFoldForm.call_amount) : null,
                notes: bigFoldForm.description,
                difficulty: bigFoldForm.difficulty,
                decision_quality: 'correct',
            });

            await refreshSession();
            setShowBigFoldModal(false);
            setBigFoldForm({ hand_log: '', call_amount: '', description: '', difficulty: 'easy' });
        } catch (err) {
            console.error('Failed to add discipline fold:', err);
        }
    };

    const handleAddCuriosityCall = async (e) => {
        e.preventDefault();
        try {
            // Map old severity values to new Hand model values
            const severityMap = { 'pretty_bad': 'moderate', 'catastrophic': 'severe' };
            const mappedSeverity = severityMap[curiosityCallForm.severity] || curiosityCallForm.severity;

            await axios.post('/api/big-hands/', {
                session: activeSession.id,
                hand_description: curiosityCallForm.hand_log,
                hand_type: 'call',
                amount: curiosityCallForm.call_amount ? parseFloat(curiosityCallForm.call_amount) : null,
                notes: curiosityCallForm.description,
                severity: mappedSeverity,
                decision_quality: 'mistake',
                tags: ['curiosity_call'],
            });

            await refreshSession();
            setShowCuriosityCallModal(false);
            setCuriosityCallForm({ hand_log: '', call_amount: '', description: '', severity: 'pretty_bad' });
        } catch (err) {
            console.error('Failed to add curiosity call:', err);
        }
    };

    const handleAddHandLog = async (e) => {
        e.preventDefault();
        try {
            // Build tags array - include user-selected tags
            const tags = [...handLogForm.tags];
            // Add review_needed if no tags selected (so it shows up for review)
            if (tags.length === 0) {
                tags.push('review_needed');
            }

            // Use unified hands endpoint
            await axios.post('/api/hands/', {
                session: activeSession.id,
                hand_description: handLogForm.description,
                outcome: handLogForm.outcome,
                amount: handLogForm.amount ? parseFloat(handLogForm.amount) : 0,
                players_involved_ids: handLogForm.players_involved_ids,
                tags: tags,
                notes: '',
            });

            await refreshSession();
            setShowHandLogModal(false);
            setHandLogForm({ description: '', amount: '', outcome: 'won', players_involved_ids: [], tags: [] });
            setNewTagName('');
        } catch (err) {
            console.error('Failed to add hand log:', err);
        }
    };

    // Quick Calling Log - submit discipline fold or curiosity call
    const handleQuickLogSubmit = async (e) => {
        e.preventDefault();
        if (!activeSession?.id || quickLogSubmitting) return;

        setQuickLogSubmitting(true);
        try {
            const villain = sessionPlayers.find(sp => sp.player?.id === parseInt(quickLogForm.villain_id));
            const villainName = villain?.player?.name || 'Villain';

            const isFold = quickLogType === 'fold';
            const tag = isFold ? 'discipline fold' : 'curiosity call';
            const outcome = isFold ? 'folded' : 'lost';
            const description = quickLogForm.hole_cards
                ? `${quickLogForm.hole_cards} vs ${villainName}`
                : `vs ${villainName}`;

            await axios.post('/api/hands/', {
                session: activeSession.id,
                hand_description: description,
                tags: [tag],
                amount: quickLogForm.amount ? parseFloat(quickLogForm.amount) : 0,
                outcome: outcome,
                players_involved_ids: quickLogForm.villain_id ? [parseInt(quickLogForm.villain_id)] : [],
                notes: '',
            });

            await refreshSession();
            setShowQuickLogModal(false);
            setQuickLogForm({ villain_id: '', hole_cards: '', amount: '' });

            // Show toast
            setQuickLogToast(isFold ? 'Fold logged' : 'Call logged');
            setTimeout(() => setQuickLogToast(null), 2000);
        } catch (err) {
            console.error('Failed to add quick log:', err);
        } finally {
            setQuickLogSubmitting(false);
        }
    };

    // Calculate quick log stats from session hands
    const getQuickLogStats = () => {
        const hands = activeSession?.big_hands || [];
        let folds = 0, foldAmount = 0;
        let calls = 0, callAmount = 0;

        hands.forEach(hand => {
            const tags = hand.tags || [];
            if (tags.includes('discipline fold')) {
                folds++;
                foldAmount += parseFloat(hand.amount || 0);
            }
            if (tags.includes('curiosity call')) {
                calls++;
                callAmount += parseFloat(hand.amount || 0);
            }
        });

        return { folds, foldAmount, calls, callAmount, net: foldAmount - callAmount };
    };

    const handleAddSessionNote = async (e) => {
        e.preventDefault();
        if (!newNoteContent.trim() || !activeSession?.id) return;

        setAddingNote(true);
        try {
            await axios.post('/api/session-notes/', {
                session: activeSession.id,
                content: newNoteContent.trim(),
            });
            setNewNoteContent('');
            await refreshSession();
        } catch (err) {
            console.error('Failed to add session note:', err);
        } finally {
            setAddingNote(false);
        }
    };

    const handleDeleteSessionNote = async (noteId) => {
        try {
            await axios.delete(`/api/session-notes/${noteId}/`);
            await refreshSession();
        } catch (err) {
            console.error('Failed to delete session note:', err);
        }
    };

    if (loading) {
        return (
            <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
                <LoadingContainer />
            </div>
        );
    }

    return (
        <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Live Session
                </h1>
                {!activeSession ? (
                    <button
                        onClick={() => setShowStartModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <PlayIcon className="h-5 w-5" />
                        Start Session
                    </button>
                ) : (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowChatPanel(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <ChatBubbleLeftRightIcon className="h-5 w-5" />
                            Coach
                        </button>
                        <button
                            onClick={handleStopSession}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            <FlagIcon className="h-5 w-5" />
                            End Session
                        </button>
                    </div>
                )}
            </div>

            {!activeSession ? (
                <div className="text-center py-12">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">No active session</p>
                    <button
                        onClick={() => setShowStartModal(true)}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        <PlayIcon className="h-6 w-6" />
                        Start New Session
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Session Header */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Location</p>
                                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {activeSession.location?.name}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Stakes</p>
                                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {activeSession.stake?.name}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Time Played</p>
                                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                                    {elapsedTime}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Break Enforcement System */}
                    <BreakEnforcement
                        sessionId={activeSession.id}
                        onSessionEnd={() => {
                            // Show end session modal to enter cash out
                            handleStopSession();
                        }}
                        onBreakComplete={() => {
                            // Refresh session to get updated break data for stop-loss calculation
                            refreshSession();
                        }}
                    />

                    {/* Initial State Check-in */}
                    {activeSession.check_in && (() => {
                        const checkIn = activeSession.check_in;
                        const riskLevel = checkIn.risk_level || 'green';
                        const riskCount = checkIn.risk_count || 0;
                        const levelColors = {
                            green: 'bg-green-500',
                            yellow: 'bg-yellow-500',
                            orange: 'bg-orange-500',
                            red: 'bg-red-600',
                        };
                        const levelLabels = {
                            green: 'PLAY',
                            yellow: 'MONITOR',
                            orange: 'SHORT SESSION',
                            red: 'HIGH RISK',
                        };

                        return (
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Session Check-in
                                    </h3>
                                    <span className={`${levelColors[riskLevel]} text-white text-xs font-bold px-2 py-1 rounded`}>
                                        {levelLabels[riskLevel]} ({riskCount} risk{riskCount !== 1 ? 's' : ''})
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        { key: 'hunger_risk', label: '🍔 Hunger' },
                                        { key: 'sleep_risk', label: '😴 Sleep' },
                                        { key: 'energy_risk', label: '⚡ Energy' },
                                        { key: 'frustration_risk', label: '😤 Frustration' },
                                        { key: 'overconfidence_risk', label: '👑 Overconfidence' },
                                    ].map((item) => {
                                        const hasRisk = checkIn[item.key];
                                        if (!hasRisk) return null;
                                        return (
                                            <span
                                                key={item.key}
                                                className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded text-xs font-medium"
                                            >
                                                {item.label}
                                            </span>
                                        );
                                    })}
                                    {riskCount === 0 && (
                                        <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded text-xs font-medium">
                                            All clear ✓
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Buy-in & Rakeback */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Buy-in Tracker */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Buy-ins
                                </h3>
                                <button
                                    onClick={() => setShowBuyInModal(true)}
                                    className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                                >
                                    <PlusIcon className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                ${parseFloat(activeSession.current_buy_in || 0).toFixed(2)}
                            </div>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                                {activeSession.buyins?.map(buyin => (
                                    <div key={buyin.id} className="flex justify-between text-sm">
                                        <span className="text-gray-600 dark:text-gray-400">
                                            {new Date(buyin.timestamp).toLocaleTimeString()}
                                        </span>
                                        <span className="font-medium text-gray-900 dark:text-white">
                                            ${parseFloat(buyin.amount || 0).toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Current P&L and Stop-Loss Tracker */}
                        {(() => {
                            const totalBuyIn = parseFloat(activeSession.current_buy_in || 0);
                            const stopLoss = parseFloat(activeSession.stop_loss || 0);

                            // Get current stack from multiple sources - pick the most recent
                            // Source 1: Breaks with stack_size_before (when break started)
                            const breaksWithStack = (activeSession.breaks || [])
                                .filter(b => b.stack_size_before != null)
                                .map(b => ({
                                    stack: parseFloat(b.stack_size_before),
                                    timestamp: new Date(b.started_at)
                                }));

                            // Source 2: Checkpoints with stack_size
                            const checkpointsWithStack = (activeSession.checkpoints || [])
                                .filter(c => c.stack_size != null)
                                .map(c => ({
                                    stack: parseFloat(c.stack_size),
                                    timestamp: new Date(c.timestamp)
                                }));

                            // Combine and sort by timestamp (most recent first)
                            const allStackRecords = [...breaksWithStack, ...checkpointsWithStack]
                                .sort((a, b) => b.timestamp - a.timestamp);

                            // Use most recent stack, or buy-in if no records
                            const hasStackRecord = allStackRecords.length > 0;
                            const currentStack = hasStackRecord ? allStackRecords[0].stack : totalBuyIn;
                            const lastUpdated = hasStackRecord ? allStackRecords[0].timestamp : null;

                            // Calculate P&L (positive = winning, negative = losing)
                            const currentPnL = currentStack - totalBuyIn;
                            const isWinning = currentPnL >= 0;

                            // Stop-loss calculation (only if stop-loss is set)
                            const losses = Math.max(0, totalBuyIn - currentStack);
                            const remaining = stopLoss - losses;
                            const isOverLimit = stopLoss > 0 && remaining <= 0;

                            return (
                                <>
                                    {/* Current P&L Card */}
                                    <div className={`border rounded-lg p-6 ${
                                        isWinning
                                            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                                            : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                                    }`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {isWinning ? '📈' : '📉'} Current P&L
                                            </h3>
                                            <button
                                                onClick={() => {
                                                    setCheckpointForm({ stack_size: '', pnl: '', notes: '' });
                                                    setEditingCheckpointId(null);
                                                    setShowCheckpointModal(true);
                                                }}
                                                className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                                            >
                                                Update Stack
                                            </button>
                                        </div>
                                        <div className={`text-3xl font-bold mb-1 ${
                                            isWinning ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                        }`}>
                                            {isWinning ? '+' : ''}${currentPnL.toFixed(2)}
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            Stack: ${currentStack.toFixed(2)} | In for: ${totalBuyIn.toFixed(2)}
                                        </div>
                                        {lastUpdated && (
                                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                Updated {lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                            </div>
                                        )}
                                        {!hasStackRecord && (
                                            <div className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                                                ⚠️ No stack recorded yet - update during breaks
                                            </div>
                                        )}
                                    </div>

                                    {/* Stop-Loss Card */}
                                    {stopLoss > 0 && (
                                        <div className={`border rounded-lg p-6 ${
                                            isOverLimit
                                                ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                        }`}>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                                🛑 Stop-Loss
                                            </h3>
                                            <div className={`text-3xl font-bold mb-2 ${
                                                isOverLimit ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                                            }`}>
                                                ${stopLoss.toFixed(2)}
                                            </div>
                                            {isOverLimit ? (
                                                <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                                                    ⚠️ You've exceeded your stop-loss by ${Math.abs(remaining).toFixed(2)}!
                                                </p>
                                            ) : (
                                                <div>
                                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                                                        <div
                                                            className={`h-2 rounded-full ${losses > stopLoss * 0.8 ? 'bg-red-500' : losses > stopLoss * 0.5 ? 'bg-amber-500' : 'bg-green-500'}`}
                                                            style={{width: `${Math.min(100, (losses / stopLoss) * 100)}%`}}
                                                        />
                                                    </div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        ${remaining.toFixed(2)} remaining (losses: ${losses.toFixed(2)})
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            );
                        })()}

                        {/* Rakeback Tracker */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Rakeback
                                </h3>
                                <button
                                    onClick={() => {
                                        setRakebackForm({ amount: activeSession.rakeback || '' });
                                        setShowRakebackModal(true);
                                    }}
                                    className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    <PencilIcon className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                                ${parseFloat(activeSession.rakeback || 0).toFixed(2)}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Earned this session
                            </p>
                        </div>
                    </div>

                    {/* Quick Calling Log - Discipline Folds & Curiosity Calls */}
                    <div className="bg-gradient-to-r from-green-50 to-red-50 dark:from-green-900/20 dark:to-red-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Calling Discipline
                        </h3>

                        {/* Tracker Bar */}
                        {(() => {
                            const stats = getQuickLogStats();
                            return (
                                <div className="flex items-center justify-between text-sm mb-4 bg-white dark:bg-gray-800 rounded-lg p-3">
                                    <div className="flex items-center gap-4">
                                        <span className="text-green-600 dark:text-green-400 font-medium">
                                            Folds: {stats.folds} (+${stats.foldAmount.toFixed(0)})
                                        </span>
                                        <span className="text-gray-300 dark:text-gray-600">|</span>
                                        <span className="text-red-600 dark:text-red-400 font-medium">
                                            Calls: {stats.calls} (-${stats.callAmount.toFixed(0)})
                                        </span>
                                    </div>
                                    <span className={`font-bold ${stats.net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                        Net: {stats.net >= 0 ? '+' : ''}${stats.net.toFixed(0)}
                                    </span>
                                </div>
                            );
                        })()}

                        {/* Quick Log Buttons */}
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => {
                                    setQuickLogType('fold');
                                    setQuickLogForm({ villain_id: '', hole_cards: '', amount: '' });
                                    setShowQuickLogModal(true);
                                }}
                                className="flex items-center justify-center gap-2 py-4 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-lg transition-colors"
                            >
                                <span className="text-2xl">💪</span>
                                Discipline Fold
                            </button>
                            <button
                                onClick={() => {
                                    setQuickLogType('call');
                                    setQuickLogForm({ villain_id: '', hole_cards: '', amount: '' });
                                    setShowQuickLogModal(true);
                                }}
                                className="flex items-center justify-center gap-2 py-4 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-lg transition-colors"
                            >
                                <span className="text-2xl">🤔</span>
                                Curiosity Call
                            </button>
                        </div>
                    </div>

                    {/* Quick Hand Log - Simple logging for review later */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Notable Hands ({activeSession.big_hands?.length || 0})
                            </h3>
                            <button
                                onClick={() => setShowHandLogModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Log Hand
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Quick log hands for later review. Categorize them off-table.
                        </p>
                        <div className="space-y-2">
                            {activeSession.big_hands?.length === 0 ? (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                    No hands logged yet
                                </p>
                            ) : (
                                activeSession.big_hands?.map(hand => (
                                    <div key={hand.id} className="flex justify-between items-start border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-900 dark:text-white">{hand.hand_description}</p>
                                            {hand.amount > 0 && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {hand.outcome_display}: ${parseFloat(hand.amount).toFixed(2)}
                                                </p>
                                            )}
                                            {hand.tags?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {hand.tags_display?.map((tag, idx) => (
                                                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-400">
                                            {new Date(hand.timestamp).toLocaleTimeString()}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Quick Session Notes */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Notes ({activeSession.session_notes?.length || 0})
                            </h3>
                        </div>
                        <form onSubmit={handleAddSessionNote} className="mb-4">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newNoteContent}
                                    onChange={(e) => setNewNoteContent(e.target.value)}
                                    placeholder="Quick note..."
                                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <button
                                    type="submit"
                                    disabled={!newNoteContent.trim() || addingNote}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <PlusIcon className="h-5 w-5" />
                                </button>
                            </div>
                        </form>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {activeSession.session_notes?.length === 0 ? (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                    No notes yet
                                </p>
                            ) : (
                                activeSession.session_notes?.map(note => (
                                    <div key={note.id} className="flex justify-between items-start border border-gray-200 dark:border-gray-600 rounded-lg p-3 group">
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-900 dark:text-white">{note.content}</p>
                                            <span className="text-xs text-gray-400">
                                                {new Date(note.created_at).toLocaleTimeString()}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteSessionNote(note.id)}
                                            className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 transition-opacity"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Legacy Advanced Logging - Hidden (now using Tags in Notable Hand instead) */}
                    {false && (
                    <>
                    {/* Mistakes Log */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Mistakes ({activeSession.mistakes?.length || 0})
                            </h3>
                            <button
                                onClick={() => setShowMistakeModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Log Mistake
                            </button>
                        </div>
                        <div className="space-y-3">
                            {activeSession.mistakes?.length === 0 ? (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                    No mistakes logged yet
                                </p>
                            ) : (
                                activeSession.mistakes?.map(mistake => (
                                    <div key={mistake.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                        {editingMistakeId === mistake.id ? (
                                            // Edit Mode
                                            <div className="space-y-3">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                            Mistake Type
                                                        </label>
                                                        <select
                                                            value={mistakeForm.mistake_type_id}
                                                            onChange={(e) => setMistakeForm({...mistakeForm, mistake_type_id: e.target.value})}
                                                            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                                                        >
                                                            {mistakeTypes.map(type => (
                                                                <option key={type.id} value={type.id}>{type.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                            Cost Estimate
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={mistakeForm.cost_estimate}
                                                            onChange={(e) => setMistakeForm({...mistakeForm, cost_estimate: e.target.value})}
                                                            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                                                            placeholder="Optional"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Related Leaks (Optional)
                                                    </label>
                                                    <div className="space-y-1 max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2">
                                                        {leaks.map(leak => (
                                                            <label key={leak.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={mistakeForm.leak_ids.includes(leak.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setMistakeForm({...mistakeForm, leak_ids: [...mistakeForm.leak_ids, leak.id]});
                                                                        } else {
                                                                            setMistakeForm({...mistakeForm, leak_ids: mistakeForm.leak_ids.filter(id => id !== leak.id)});
                                                                        }
                                                                    }}
                                                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                                />
                                                                <span className="text-xs text-gray-700 dark:text-gray-300">{leak.name}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Description
                                                    </label>
                                                    <textarea
                                                        value={mistakeForm.description}
                                                        onChange={(e) => setMistakeForm({...mistakeForm, description: e.target.value})}
                                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                                                        rows="2"
                                                    />
                                                </div>
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={handleCancelEditMistake}
                                                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={() => handleSaveMistake(mistake.id)}
                                                        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                                                    >
                                                        Save
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            // View Mode
                                            <>
                                                <div className="flex items-start justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className="w-3 h-3 rounded-full"
                                                            style={{ backgroundColor: mistake.mistake_type_color || '#f97316' }}
                                                        ></div>
                                                        <span className="font-medium text-gray-900 dark:text-white">
                                                            {mistake.mistake_type_name}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                                            {new Date(mistake.timestamp).toLocaleTimeString()}
                                                        </span>
                                                        <button
                                                            onClick={() => handleEditMistake(mistake)}
                                                            className="p-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                                                            title="Edit mistake"
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => setDeleteMistakeId(mistake.id)}
                                                            className="p-1 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                                            title="Delete mistake"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                                    {mistake.description}
                                                </p>
                                                {mistake.leak_names && mistake.leak_names.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                        {mistake.leak_names.map((leakName, idx) => (
                                                            <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                                                {leakName}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {mistake.cost_estimate && (
                                                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                                        Cost: ${parseFloat(mistake.cost_estimate || 0).toFixed(2)}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Checkpoints */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Checkpoints ({activeSession.checkpoints?.length || 0})
                            </h3>
                            <button
                                onClick={() => setShowCheckpointModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Add Checkpoint
                            </button>
                        </div>
                        <div className="space-y-3">
                            {activeSession.checkpoints?.length === 0 ? (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                    No checkpoints added yet
                                </p>
                            ) : (
                                activeSession.checkpoints?.map(checkpoint => {
                                    return (
                                        <div key={checkpoint.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                            <div className="flex items-start justify-between mb-2">
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    {new Date(checkpoint.timestamp).toLocaleTimeString()}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleEditCheckpoint(checkpoint)}
                                                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                                        title="Edit checkpoint"
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteCheckpointId(checkpoint.id)}
                                                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                                        title="Delete checkpoint"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 mb-2">
                                                <div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">Stack Size</p>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        ${parseFloat(checkpoint.stack_size || 0).toFixed(2)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">P&L</p>
                                                    <p className={`text-sm font-medium ${
                                                        checkpoint.pnl > 0
                                                            ? 'text-green-600 dark:text-green-400'
                                                            : checkpoint.pnl < 0
                                                                ? 'text-red-600 dark:text-red-400'
                                                                : 'text-gray-600 dark:text-gray-400'
                                                    }`}>
                                                        ${parseFloat(checkpoint.pnl || 0).toFixed(2)}
                                                    </p>
                                                </div>
                                            </div>
                                            {checkpoint.notes && (
                                                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                                                    {checkpoint.notes}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Discipline Folds - filtered from big_hands */}
                    {(() => {
                        const disciplineFolds = activeSession.big_hands?.filter(h =>
                            h.hand_type === 'fold' && h.decision_quality === 'correct'
                        ) || [];
                        return (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Discipline Folds ({disciplineFolds.length})
                            </h3>
                            <button
                                onClick={() => setShowBigFoldModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Add Discipline Fold
                            </button>
                        </div>
                        <div className="space-y-3">
                            {disciplineFolds.length === 0 ? (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                    No discipline folds logged yet
                                </p>
                            ) : (
                                disciplineFolds.map(fold => (
                                    <div key={fold.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {new Date(fold.timestamp).toLocaleTimeString('en-US', {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                                {fold.difficulty_display && (
                                                    <span className={`text-xs font-semibold px-2 py-1 rounded ${
                                                        fold.difficulty === 'hard'
                                                            ? 'bg-purple-200 dark:bg-purple-800/50 text-purple-800 dark:text-purple-200'
                                                            : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                                                    }`}>
                                                        {fold.difficulty_display}
                                                    </span>
                                                )}
                                            </div>
                                            {fold.amount && (
                                                <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                                                    Saved: ${parseFloat(fold.amount).toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mb-2">
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Hand Details:</p>
                                            <p className="text-sm text-gray-900 dark:text-white font-medium whitespace-pre-wrap">
                                                {fold.hand_description}
                                            </p>
                                        </div>
                                        {fold.notes && (
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reasoning:</p>
                                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                                    {fold.notes}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                        );
                    })()}

                    {/* Curiosity Calls - filtered from big_hands */}
                    {(() => {
                        const curiosityCalls = activeSession.big_hands?.filter(h =>
                            h.tags?.includes('curiosity_call') ||
                            (h.hand_type === 'call' && h.decision_quality === 'mistake')
                        ) || [];
                        return (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Curiosity Calls ({curiosityCalls.length})
                            </h3>
                            <button
                                onClick={() => setShowCuriosityCallModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Log Curiosity Call
                            </button>
                        </div>
                        <div className="space-y-3">
                            {curiosityCalls.length === 0 ? (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                    No curiosity calls logged yet
                                </p>
                            ) : (
                                curiosityCalls.map(call => (
                                    <div key={call.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {new Date(call.timestamp).toLocaleTimeString('en-US', {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                                <span className={`text-xs font-semibold px-2 py-1 rounded ${
                                                    call.severity === 'severe'
                                                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                                        : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                                }`}>
                                                    {call.severity_display || 'Bad Call'}
                                                </span>
                                            </div>
                                            {call.amount && (
                                                <span className="text-sm font-semibold text-cyan-600 dark:text-cyan-400">
                                                    Lost: ${parseFloat(call.amount).toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mb-2">
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Hand Details:</p>
                                            <p className="text-sm text-gray-900 dark:text-white font-medium whitespace-pre-wrap">
                                                {call.hand_description}
                                            </p>
                                        </div>
                                        {call.notes && (
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Why / Outcome:</p>
                                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                                    {call.notes}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                        );
                    })()}
                    </>
                    )}

                    {/* Players at Table */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Players at Table ({sessionPlayers.filter(p => p.is_still_playing).length} active, {sessionPlayers.filter(p => !p.is_still_playing).length} left)
                            </h3>
                            <button
                                onClick={() => setShowAddPlayerModal(true)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                <PlusIcon className="h-4 w-4" />
                                Add Player
                            </button>
                        </div>
                        {sessionPlayers.length === 0 ? (
                            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">
                                No players added yet
                            </p>
                        ) : (
                            <>
                                {/* Active Players */}
                                {sessionPlayers.filter(p => p.is_still_playing).length > 0 && (
                                    <div className="mb-4">
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Active Players</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {sessionPlayers.filter(p => p.is_still_playing).map(sessionPlayer => {
                                                const category = sessionPlayer.player.player_category || 'unknown';
                                                const categoryColors = {
                                                    shark: 'border-l-black bg-gray-50 dark:bg-gray-900/50',
                                                    reg: 'border-l-red-500 bg-red-50 dark:bg-red-900/20',
                                                    fish: 'border-l-green-500 bg-green-50 dark:bg-green-900/20',
                                                    whale: 'border-l-pink-500 bg-pink-50 dark:bg-pink-900/20',
                                                    unknown: 'border-l-gray-400 bg-gray-50 dark:bg-gray-700/50',
                                                };
                                                const avatarColors = {
                                                    shark: 'bg-black',
                                                    reg: 'bg-red-500',
                                                    fish: 'bg-green-500',
                                                    whale: 'bg-pink-500',
                                                    unknown: 'bg-gray-400',
                                                };
                                                return (
                                                    <div
                                                        key={sessionPlayer.id}
                                                        className={`p-3 border border-gray-200 dark:border-gray-600 border-l-4 rounded-lg hover:shadow-md transition-all ${categoryColors[category]}`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            {/* Profile Picture */}
                                                            <div
                                                                onClick={() => navigate(`/players/${sessionPlayer.player.id}`)}
                                                                className={`w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold cursor-pointer overflow-hidden ${avatarColors[category]}`}
                                                            >
                                                                {sessionPlayer.player.profile_picture_url ? (
                                                                    <img
                                                                        src={sessionPlayer.player.profile_picture_url}
                                                                        alt={sessionPlayer.player.name}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <span className="text-lg">{sessionPlayer.player.name?.[0]?.toUpperCase() || '?'}</span>
                                                                )}
                                                            </div>

                                                            {/* Player Info */}
                                                            <div className="flex-1 min-w-0">
                                                                <div
                                                                    onClick={() => navigate(`/players/${sessionPlayer.player.id}`)}
                                                                    className="cursor-pointer"
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <p className="font-semibold text-gray-900 dark:text-white truncate">{sessionPlayer.player.name}</p>
                                                                        <PlayerTypeIcon playerType={category} size="w-3 h-3" />
                                                                    </div>
                                                                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                                                        {sessionPlayer.seat_position && <span>Seat {sessionPlayer.seat_position}</span>}
                                                                        <span>Joined {new Date(sessionPlayer.joined_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleMarkPlayerLeft(sessionPlayer.id)}
                                                                    className="mt-2 px-2 py-1 text-xs text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                                                                >
                                                                    Mark as Left
                                                                </button>
                                                            </div>

                                                            {/* Remove Button */}
                                                            <button
                                                                onClick={() => handleRemovePlayer(sessionPlayer.id)}
                                                                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                            >
                                                                <XMarkIcon className="h-5 w-5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Left Players */}
                                {sessionPlayers.filter(p => !p.is_still_playing).length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Left Table</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {sessionPlayers.filter(p => !p.is_still_playing).map(sessionPlayer => {
                                                const category = sessionPlayer.player.player_category || 'unknown';
                                                const avatarColors = {
                                                    shark: 'bg-black',
                                                    reg: 'bg-red-500',
                                                    fish: 'bg-green-500',
                                                    whale: 'bg-pink-500',
                                                    unknown: 'bg-gray-400',
                                                };
                                                return (
                                                    <div
                                                        key={sessionPlayer.id}
                                                        className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700/50 opacity-60"
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            {/* Profile Picture */}
                                                            <div
                                                                onClick={() => navigate(`/players/${sessionPlayer.player.id}`)}
                                                                className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold cursor-pointer overflow-hidden ${avatarColors[category]} grayscale`}
                                                            >
                                                                {sessionPlayer.player.profile_picture_url ? (
                                                                    <img
                                                                        src={sessionPlayer.player.profile_picture_url}
                                                                        alt={sessionPlayer.player.name}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <span>{sessionPlayer.player.name?.[0]?.toUpperCase() || '?'}</span>
                                                                )}
                                                            </div>

                                                            {/* Player Info */}
                                                            <div className="flex-1 min-w-0">
                                                                <div
                                                                    onClick={() => navigate(`/players/${sessionPlayer.player.id}`)}
                                                                    className="cursor-pointer"
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <p className="font-medium text-gray-600 dark:text-gray-400 truncate">{sessionPlayer.player.name}</p>
                                                                        <PlayerTypeIcon playerType={category} size="w-3 h-3" />
                                                                    </div>
                                                                    <p className="text-xs text-gray-400 dark:text-gray-500">
                                                                        {new Date(sessionPlayer.joined_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {new Date(sessionPlayer.left_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                                    </p>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleBringPlayerBack(sessionPlayer.id)}
                                                                    className="mt-1 px-2 py-0.5 text-xs text-green-600 bg-green-50 border border-green-300 rounded hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-900/50"
                                                                >
                                                                    Bring Back
                                                                </button>
                                                            </div>

                                                            {/* Remove Button */}
                                                            <button
                                                                onClick={() => handleRemovePlayer(sessionPlayer.id)}
                                                                className="text-gray-400 hover:text-red-500"
                                                            >
                                                                <XMarkIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Start Session Modal */}
            {showStartModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Start Session</h2>
                            <button onClick={() => setShowStartModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <form onSubmit={handleStartSession} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Location
                                </label>
                                <select
                                    value={startForm.location_id}
                                    onChange={(e) => setStartForm({...startForm, location_id: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                >
                                    <option value="">Select location...</option>
                                    {locations.map(loc => (
                                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Stakes
                                </label>
                                <select
                                    value={startForm.stake_id}
                                    onChange={(e) => setStartForm({...startForm, stake_id: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                >
                                    <option value="">Select stakes...</option>
                                    {stakes.map(stake => (
                                        <option key={stake.id} value={stake.id}>{stake.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Initial Buy-in
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={startForm.buy_in}
                                    onChange={(e) => setStartForm({...startForm, buy_in: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Notes (optional)
                                </label>
                                <textarea
                                    value={startForm.notes}
                                    onChange={(e) => setStartForm({...startForm, notes: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="3"
                                    placeholder="Any initial observations or goals for this session..."
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                Start Session
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Pre-Session Ready Check Modal */}
            {showReadyCheck && (() => {
                // Calculate risk count and level
                const riskCount = [
                    checkInState.hungerRisk,
                    checkInState.sleepRisk,
                    checkInState.energyRisk,
                    checkInState.frustrationRisk,
                    checkInState.overconfidenceRisk,
                ].filter(Boolean).length;

                const riskLevel = riskCount === 0 ? 'green' : riskCount === 1 ? 'yellow' : riskCount === 2 ? 'orange' : 'red';
                const riskLabels = {
                    green: { text: 'PLAY', bg: 'bg-green-500' },
                    yellow: { text: 'MONITOR', bg: 'bg-yellow-500' },
                    orange: { text: 'SHORT SESSION ONLY', bg: 'bg-orange-500' },
                    red: { text: 'DO NOT START', bg: 'bg-red-600' },
                };
                const canStart = riskLevel !== 'red';

                return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full my-4">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">🎯 Session Check-In</h2>
                            <button onClick={handleCancelReadyCheck}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Check any risks that apply. Be honest.
                        </p>

                        {/* Risk Level Indicator */}
                        <div className={`${riskLabels[riskLevel].bg} text-white p-4 rounded-lg mb-4 text-center`}>
                            <div className="text-2xl font-bold">{riskLabels[riskLevel].text}</div>
                            <div className="text-sm opacity-90">{riskCount} risk{riskCount !== 1 ? 's' : ''} identified</div>
                        </div>

                        <div className="space-y-2 mb-6">
                            {/* Risk Checkboxes */}
                            {[
                                { key: 'hungerRisk', label: 'Hunger Risk', emoji: '🍔', description: 'Hungry or low blood sugar' },
                                { key: 'sleepRisk', label: 'Sleep Risk', emoji: '😴', description: 'Tired or sleep deprived' },
                                { key: 'energyRisk', label: 'Over Energy Risk', emoji: '⚡', description: 'Restless or over-caffeinated' },
                                { key: 'frustrationRisk', label: 'Anger / Frustration Risk', emoji: '😤', description: 'Already irritated or tilted' },
                                { key: 'overconfidenceRisk', label: 'Over Confidence Risk', emoji: '👑', description: 'Feeling invincible or cocky' },
                            ].map((item) => (
                                <div key={item.key} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">{item.emoji}</span>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setCheckInState({...checkInState, [item.key]: true})}
                                            className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all ${
                                                checkInState[item.key]
                                                    ? 'bg-red-500 text-white'
                                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                                            }`}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCheckInState({...checkInState, [item.key]: false})}
                                            className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all ${
                                                !checkInState[item.key]
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                                            }`}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {/* Stop-loss amount */}
                            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 mt-4">
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">🛑</span>
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900 dark:text-white">Stop-loss amount</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">How much are you willing to lose?</p>
                                    </div>
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="text-gray-500 dark:text-gray-400">$</span>
                                    <input
                                        type="number"
                                        value={stopLossAmount}
                                        onChange={(e) => setStopLossAmount(e.target.value)}
                                        placeholder="e.g. 500"
                                        className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        min="0"
                                        step="1"
                                    />
                                </div>
                            </div>

                            {/* Notifications checkbox */}
                            <label className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                                <input
                                    type="checkbox"
                                    checked={checkInState.notificationsOff}
                                    onChange={(e) => setCheckInState({...checkInState, notificationsOff: e.target.checked})}
                                    className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">🔕 Notifications off</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">DND mode enabled</p>
                                </div>
                            </label>

                            {/* Core Session Reminders */}
                            <div className="mt-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xl">🧠</span>
                                    <p className="font-semibold text-blue-800 dark:text-blue-200">Core Reminders</p>
                                </div>
                                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-2">
                                    <li className="flex items-start gap-2">
                                        <span>⏱️</span>
                                        <span><strong>First hour:</strong> Strictly ABC poker. Observe and warm up only.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span>🐟</span>
                                        <span><strong>Fish at the table:</strong> Value oriented. No overconfidence bluffs or curiosity calls.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span>🎯</span>
                                        <span><strong>Preflop:</strong> Stick to strict TAG guidelines.</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Pre-Session Instructions */}
                            {breakConfig?.presession_instructions && (
                                <div className="mt-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xl">📋</span>
                                        <p className="font-semibold text-amber-800 dark:text-amber-200">Session Rules</p>
                                    </div>
                                    <div className="text-sm text-amber-700 dark:text-amber-300 whitespace-pre-line">
                                        {breakConfig.presession_instructions}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleCancelReadyCheck}
                                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleConfirmReady}
                                disabled={!allReadyItemsChecked || !canStart}
                                className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                                    !canStart
                                        ? 'bg-red-600 text-white cursor-not-allowed opacity-75'
                                        : allReadyItemsChecked
                                            ? `${riskLabels[riskLevel].bg} text-white hover:opacity-90`
                                            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                }`}
                            >
                                {!canStart ? '❌ Too Many Risks' : allReadyItemsChecked ? "Let's Go! 🚀" : 'Enable DND & set stop-loss'}
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* Buy-in Modal */}
            {showBuyInModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Buy-in</h2>
                            <button onClick={() => setShowBuyInModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <form onSubmit={handleAddBuyIn} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Amount
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={buyInForm.amount}
                                    onChange={(e) => setBuyInForm({...buyInForm, amount: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Notes (optional)
                                </label>
                                <input
                                    type="text"
                                    value={buyInForm.notes}
                                    onChange={(e) => setBuyInForm({...buyInForm, notes: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                                Add Buy-in
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Rakeback Modal */}
            {showRakebackModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Update Rakeback</h2>
                            <button onClick={() => setShowRakebackModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateRakeback} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Rakeback Amount
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={rakebackForm.amount}
                                    onChange={(e) => setRakebackForm({...rakebackForm, amount: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    placeholder="0.00"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                Update Rakeback
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Quick Hand Log Modal */}
            {showHandLogModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Log Notable Hand</h2>
                            <button onClick={() => setShowHandLogModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Quick log for review later. Add tags to categorize.
                        </p>
                        <form onSubmit={handleAddHandLog} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    What happened?
                                </label>
                                <textarea
                                    value={handLogForm.description}
                                    onChange={(e) => setHandLogForm({...handLogForm, description: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    placeholder="e.g., AK vs QQ all-in pre, set over set on flop..."
                                    rows="3"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Result
                                    </label>
                                    <select
                                        value={handLogForm.outcome}
                                        onChange={(e) => setHandLogForm({...handLogForm, outcome: e.target.value})}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    >
                                        <option value="won">Won</option>
                                        <option value="lost">Lost</option>
                                        <option value="chopped">Chopped</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Amount (optional)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={handLogForm.amount}
                                        onChange={(e) => setHandLogForm({...handLogForm, amount: e.target.value})}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                        placeholder="$"
                                    />
                                </div>
                            </div>
                            {/* Villains involved */}
                            {sessionPlayers.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Villains involved (optional)
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {sessionPlayers.map(sp => (
                                            <button
                                                key={sp.player.id}
                                                type="button"
                                                onClick={() => {
                                                    const ids = handLogForm.players_involved_ids;
                                                    if (ids.includes(sp.player.id)) {
                                                        setHandLogForm({...handLogForm, players_involved_ids: ids.filter(id => id !== sp.player.id)});
                                                    } else {
                                                        setHandLogForm({...handLogForm, players_involved_ids: [...ids, sp.player.id]});
                                                    }
                                                }}
                                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                                    handLogForm.players_involved_ids.includes(sp.player.id)
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                }`}
                                            >
                                                {sp.player.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Tags */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Tags (optional)
                                </label>
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {handTags.map(tag => (
                                        <button
                                            key={tag.id}
                                            type="button"
                                            onClick={() => {
                                                const tags = handLogForm.tags;
                                                if (tags.includes(tag.name)) {
                                                    setHandLogForm({...handLogForm, tags: tags.filter(t => t !== tag.name)});
                                                } else {
                                                    setHandLogForm({...handLogForm, tags: [...tags, tag.name]});
                                                }
                                            }}
                                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                                handLogForm.tags.includes(tag.name)
                                                    ? 'bg-purple-600 text-white'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            {tag.name}
                                        </button>
                                    ))}
                                </div>
                                {/* Create new tag inline */}
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newTagName}
                                        onChange={(e) => setNewTagName(e.target.value)}
                                        placeholder="New tag name..."
                                        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (newTagName.trim()) {
                                                const tag = await createHandTag(newTagName.trim());
                                                if (tag) {
                                                    setHandLogForm({...handLogForm, tags: [...handLogForm.tags, tag.name]});
                                                    setNewTagName('');
                                                }
                                            }
                                        }}
                                        disabled={!newTagName.trim()}
                                        className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Log Hand
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Quick Calling Log Modal */}
            {showQuickLogModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {quickLogType === 'fold' ? '💪 Discipline Fold' : '🤔 Curiosity Call'}
                            </h2>
                            <button onClick={() => setShowQuickLogModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <form onSubmit={handleQuickLogSubmit} className="space-y-4">
                            {/* Villain */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Villain
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {sessionPlayers.filter(sp => sp.is_still_playing).map(sp => (
                                        <button
                                            key={sp.player.id}
                                            type="button"
                                            onClick={() => setQuickLogForm({...quickLogForm, villain_id: sp.player.id.toString()})}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                quickLogForm.villain_id === sp.player.id.toString()
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            {sp.player.cached_profile_url && (
                                                <img
                                                    src={sp.player.cached_profile_url}
                                                    alt=""
                                                    className="w-6 h-6 rounded-full object-cover"
                                                />
                                            )}
                                            {sp.player.name}
                                        </button>
                                    ))}
                                    {sessionPlayers.filter(sp => sp.is_still_playing).length === 0 && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">No players at table</p>
                                    )}
                                </div>
                            </div>

                            {/* Hole Cards */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Hole Cards
                                </label>
                                <input
                                    type="text"
                                    value={quickLogForm.hole_cards}
                                    onChange={(e) => setQuickLogForm({...quickLogForm, hole_cards: e.target.value.toUpperCase()})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white text-lg"
                                    placeholder="e.g., AKo, JJ, T9s"
                                    autoFocus
                                />
                            </div>

                            {/* Amount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {quickLogType === 'fold' ? 'Amount Saved' : 'Amount Lost'}
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                    <input
                                        type="number"
                                        step="1"
                                        value={quickLogForm.amount}
                                        onChange={(e) => setQuickLogForm({...quickLogForm, amount: e.target.value})}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pl-7 pr-3 py-2 text-gray-900 dark:text-white text-lg"
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={quickLogSubmitting}
                                className={`w-full px-4 py-3 text-white rounded-lg font-medium text-lg transition-colors ${
                                    quickLogType === 'fold'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                } disabled:opacity-50`}
                            >
                                {quickLogSubmitting ? 'Saving...' : 'Log'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Quick Log Toast */}
            {quickLogToast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
                    <div className={`px-6 py-3 rounded-full text-white font-medium shadow-lg ${
                        quickLogToast.includes('Fold') ? 'bg-green-600' : 'bg-red-600'
                    }`}>
                        {quickLogToast} {quickLogToast.includes('Fold') ? '💪' : ''}
                    </div>
                </div>
            )}

            {/* Mistake Modal */}
            {showMistakeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Log Mistake</h2>
                            <button onClick={() => setShowMistakeModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <form onSubmit={handleAddMistake} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Mistake Type
                                </label>
                                <select
                                    value={mistakeForm.mistake_type_id}
                                    onChange={(e) => setMistakeForm({...mistakeForm, mistake_type_id: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                >
                                    {mistakeTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Related Leaks (Optional)
                                </label>
                                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2">
                                    {leaks.map(leak => (
                                        <label key={leak.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded">
                                            <input
                                                type="checkbox"
                                                checked={mistakeForm.leak_ids.includes(leak.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setMistakeForm({...mistakeForm, leak_ids: [...mistakeForm.leak_ids, leak.id]});
                                                    } else {
                                                        setMistakeForm({...mistakeForm, leak_ids: mistakeForm.leak_ids.filter(id => id !== leak.id)});
                                                    }
                                                }}
                                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{leak.name}</span>
                                        </label>
                                    ))}
                                    {leaks.length === 0 && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">No leaks defined yet</p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={mistakeForm.description}
                                    onChange={(e) => setMistakeForm({...mistakeForm, description: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="3"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Cost Estimate (optional)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={mistakeForm.cost_estimate}
                                    onChange={(e) => setMistakeForm({...mistakeForm, cost_estimate: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                />
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="is_curiosity_call"
                                    checked={mistakeForm.is_curiosity_call}
                                    onChange={(e) => setMistakeForm({...mistakeForm, is_curiosity_call: e.target.checked})}
                                    className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 mr-2"
                                />
                                <label htmlFor="is_curiosity_call" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Mark as curiosity call
                                </label>
                            </div>
                            <button
                                type="submit"
                                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                            >
                                Log Mistake
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Player Modal */}
            {showAddPlayerModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Player to Table</h2>
                            <button onClick={() => {
                                setShowAddPlayerModal(false);
                                setShowAllLocations(false);
                                setShowNewPlayerForm(false);
                            }}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>

                        {!showNewPlayerForm ? (
                            showAllLocations ? (
                                /* All Players View */
                                <>
                                    <button
                                        onClick={() => {
                                            setShowAllLocations(false);
                                            fetchAtTablePlayers(activeSession?.location?.id, '');
                                        }}
                                        className="mb-3 text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                                    >
                                        ← Back to quick search
                                    </button>
                                    <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2">
                                        {loadingPlayers ? (
                                            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-primary-600 mb-2"></div>
                                                <p className="text-sm">Loading players...</p>
                                            </div>
                                        ) : allPlayers.length > 0 ? (
                                            allPlayers.map(player => {
                                                const isAtTable = sessionPlayers.some(sp => sp.player?.id === player.id && !sp.left_at);
                                                return (
                                                    <div
                                                        key={`all-${player.id}`}
                                                        className={`flex items-center justify-between p-2 rounded-lg ${
                                                            isAtTable
                                                                ? 'bg-gray-100 dark:bg-gray-700 opacity-60'
                                                                : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-primary-600 text-white overflow-hidden">
                                                                {player.profile_picture_url ? (
                                                                    <img src={player.profile_picture_url} alt="" className="w-full h-full object-cover" />
                                                                ) : (
                                                                    player.name?.[0]?.toUpperCase() || '?'
                                                                )}
                                                            </div>
                                                            <div>
                                                                <div className="font-medium text-gray-900 dark:text-white text-sm">
                                                                    {player.name}
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {player.session_count || 0} session{player.session_count !== 1 ? 's' : ''}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {isAtTable ? (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400 px-2">At table</span>
                                                        ) : (
                                                            <button
                                                                onClick={async () => {
                                                                    try {
                                                                        await axios.post('/api/session-players/', {
                                                                            session: activeSession.id,
                                                                            player_id: player.id,
                                                                        });
                                                                        await refreshSession();
                                                                        await fetchSessionPlayers();
                                                                    } catch (err) {
                                                                        console.error('Failed to add player:', err);
                                                                    }
                                                                }}
                                                                className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                                                            >
                                                                Add
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                                                <p className="text-sm">No players found</p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                            /* Quick Search View */
                            <>
                                {/* Search Input */}
                                <div className="mb-4">
                                    <input
                                        type="text"
                                        value={playerSearchQuery}
                                        onChange={(e) => handlePlayerSearch(e.target.value)}
                                        placeholder="Search players..."
                                        autoFocus
                                        className="w-full px-4 py-3 text-lg rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Player Cards List */}
                                <div className="space-y-2 max-h-72 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2">
                                    {loadingPlayers ? (
                                        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-primary-600 mb-2"></div>
                                            <p className="text-sm">Loading players...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Recent Players Section */}
                                            {recentPlayers.length > 0 && !playerSearchQuery && (
                                                <>
                                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-1 py-1">
                                                        Recent at {activeSession?.location?.name || 'this location'}
                                                    </div>
                                                    {recentPlayers.map(player => {
                                                        const isAtTable = sessionPlayers.some(sp => sp.player?.id === player.id && !sp.left_at);
                                                        return (
                                                            <div
                                                                key={`recent-${player.id}`}
                                                                className={`flex items-center justify-between p-2 rounded-lg ${
                                                                    isAtTable
                                                                        ? 'bg-gray-100 dark:bg-gray-700 opacity-60'
                                                                        : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600 active:bg-gray-200 dark:active:bg-gray-500'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-primary-600 text-white overflow-hidden">
                                                                        {player.profile_picture_url ? (
                                                                            <img src={player.profile_picture_url} alt="" className="w-full h-full object-cover" />
                                                                        ) : (
                                                                            player.name?.[0]?.toUpperCase() || '?'
                                                                        )}
                                                                    </div>
                                                                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                                                                        {player.name}
                                                                    </div>
                                                                </div>
                                                                {isAtTable ? (
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400 px-2">At table</span>
                                                                ) : (
                                                                    <button
                                                                        onClick={async () => {
                                                                            try {
                                                                                await axios.post('/api/session-players/', {
                                                                                    session: activeSession.id,
                                                                                    player_id: player.id,
                                                                                });
                                                                                await refreshSession();
                                                                                await fetchSessionPlayers();
                                                                                await fetchAtTablePlayers(activeSession?.location?.id, '');
                                                                            } catch (err) {
                                                                                console.error('Failed to add player:', err);
                                                                            }
                                                                        }}
                                                                        className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 active:bg-primary-800"
                                                                    >
                                                                        Add
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}

                                            {/* Search Results Section */}
                                            {playerSearchQuery && searchResults.length > 0 && (
                                                <>
                                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-1 py-1">
                                                        Search results
                                                    </div>
                                                    {searchResults.map(player => {
                                                        const isAtTable = sessionPlayers.some(sp => sp.player?.id === player.id && !sp.left_at);
                                                        return (
                                                            <div
                                                                key={`search-${player.id}`}
                                                                className={`flex items-center justify-between p-2 rounded-lg ${
                                                                    isAtTable
                                                                        ? 'bg-gray-100 dark:bg-gray-700 opacity-60'
                                                                        : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600 active:bg-gray-200 dark:active:bg-gray-500'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-primary-600 text-white overflow-hidden">
                                                                        {player.profile_picture_url ? (
                                                                            <img src={player.profile_picture_url} alt="" className="w-full h-full object-cover" />
                                                                        ) : (
                                                                            player.name?.[0]?.toUpperCase() || '?'
                                                                        )}
                                                                    </div>
                                                                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                                                                        {player.name}
                                                                    </div>
                                                                </div>
                                                                {isAtTable ? (
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400 px-2">At table</span>
                                                                ) : (
                                                                    <button
                                                                        onClick={async () => {
                                                                            try {
                                                                                await axios.post('/api/session-players/', {
                                                                                    session: activeSession.id,
                                                                                    player_id: player.id,
                                                                                });
                                                                                await refreshSession();
                                                                                await fetchSessionPlayers();
                                                                                setPlayerSearchQuery('');
                                                                                await fetchAtTablePlayers(activeSession?.location?.id, '');
                                                                            } catch (err) {
                                                                                console.error('Failed to add player:', err);
                                                                            }
                                                                        }}
                                                                        className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 active:bg-primary-800"
                                                                    >
                                                                        Add
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}

                                            {/* Recent players when searching (shown after search results) */}
                                            {playerSearchQuery && recentPlayers.length > 0 && (
                                                <>
                                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-1 py-1 mt-2">
                                                        Recent
                                                    </div>
                                                    {recentPlayers.map(player => {
                                                        const isAtTable = sessionPlayers.some(sp => sp.player?.id === player.id && !sp.left_at);
                                                        return (
                                                            <div
                                                                key={`recent-search-${player.id}`}
                                                                className={`flex items-center justify-between p-2 rounded-lg ${
                                                                    isAtTable
                                                                        ? 'bg-gray-100 dark:bg-gray-700 opacity-60'
                                                                        : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600 active:bg-gray-200 dark:active:bg-gray-500'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-primary-600 text-white overflow-hidden">
                                                                        {player.profile_picture_url ? (
                                                                            <img src={player.profile_picture_url} alt="" className="w-full h-full object-cover" />
                                                                        ) : (
                                                                            player.name?.[0]?.toUpperCase() || '?'
                                                                        )}
                                                                    </div>
                                                                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                                                                        {player.name}
                                                                    </div>
                                                                </div>
                                                                {isAtTable ? (
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400 px-2">At table</span>
                                                                ) : (
                                                                    <button
                                                                        onClick={async () => {
                                                                            try {
                                                                                await axios.post('/api/session-players/', {
                                                                                    session: activeSession.id,
                                                                                    player_id: player.id,
                                                                                });
                                                                                await refreshSession();
                                                                                await fetchSessionPlayers();
                                                                                setPlayerSearchQuery('');
                                                                                await fetchAtTablePlayers(activeSession?.location?.id, '');
                                                                            } catch (err) {
                                                                                console.error('Failed to add player:', err);
                                                                            }
                                                                        }}
                                                                        className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 active:bg-primary-800"
                                                                    >
                                                                        Add
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}

                                            {/* Empty state */}
                                            {recentPlayers.length === 0 && searchResults.length === 0 && (
                                                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                                                    <p className="text-sm">
                                                        {playerSearchQuery
                                                            ? `No players found for "${playerSearchQuery}"`
                                                            : `No recent players at ${activeSession?.location?.name || 'this location'}`
                                                        }
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Browse All Players */}
                                <button
                                    onClick={() => {
                                        setShowAllLocations(true);
                                        fetchAllPlayers();
                                    }}
                                    className="w-full mt-3 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center justify-center gap-1"
                                >
                                    🌐 Browse all players
                                </button>

                                {/* Divider and Create New */}
                                <div className="relative my-3">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                            or
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setShowNewPlayerForm(true)}
                                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                                >
                                    <PlusIcon className="h-5 w-5" />
                                    Create New Player
                                </button>
                            </>
                            )
                        ) : (
                            <>
                                {/* Back button */}
                                <button
                                    onClick={() => setShowNewPlayerForm(false)}
                                    className="mb-4 text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                                >
                                    ← Back to player list
                                </button>

                                {/* New Player Form */}
                                <form onSubmit={handleCreateAndAddPlayer} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Seat Position
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="10"
                                                value={seatPosition}
                                                onChange={(e) => setSeatPosition(e.target.value)}
                                                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                                placeholder="1-10"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Category
                                            </label>
                                            <select
                                                value={newPlayerForm.player_category}
                                                onChange={(e) => setNewPlayerForm({...newPlayerForm, player_category: e.target.value})}
                                                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                            >
                                                <option value="unknown">Unknown</option>
                                                <option value="fish">Fish</option>
                                                <option value="whale">Whale</option>
                                                <option value="nit">Nit</option>
                                                <option value="reg">Reg</option>
                                                <option value="shark">Shark</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Description <span className="text-xs text-gray-500">(for identification)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={newPlayerForm.description}
                                            onChange={(e) => setNewPlayerForm({...newPlayerForm, description: e.target.value})}
                                            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                            placeholder="e.g., bald guy, red hoodie, young Asian woman"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Name <span className="text-xs text-gray-500">(optional - leave empty for anonymous)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={newPlayerForm.name}
                                            onChange={(e) => setNewPlayerForm({...newPlayerForm, name: e.target.value})}
                                            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                            placeholder="Leave empty to auto-generate"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {!newPlayerForm.name && newPlayerForm.description
                                            ? `Will be saved as: "${newPlayerForm.description}"`
                                            : !newPlayerForm.name
                                            ? 'Will be saved as: "player_XXX"'
                                            : `Will be saved as: "${newPlayerForm.name}"`}
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Photo <span className="text-xs text-gray-500">(optional)</span>
                                        </label>
                                        <div className="flex items-center gap-3">
                                            {picturePreview ? (
                                                <div className="relative">
                                                    <img
                                                        src={picturePreview}
                                                        alt="Preview"
                                                        className="h-16 w-16 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setPicturePreview(null);
                                                            setNewPlayerForm({...newPlayerForm, profile_picture: null});
                                                        }}
                                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                                                    >
                                                        <XMarkIcon className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="h-16 w-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                                    <CameraIcon className="h-6 w-6 text-gray-400" />
                                                </div>
                                            )}
                                            <label className="cursor-pointer px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm">
                                                {picturePreview ? 'Change Photo' : 'Add Photo'}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            setNewPlayerForm({...newPlayerForm, profile_picture: file});
                                                            setPicturePreview(URL.createObjectURL(file));
                                                        }
                                                    }}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                    >
                                        Create & Add to Table
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* End Session Modal */}
            {showEndModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">End Session</h2>
                            <button onClick={() => setShowEndModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <form onSubmit={handleEndSession} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Cash Out Amount *
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={endSessionForm.cash_out}
                                    onChange={(e) => setEndSessionForm({...endSessionForm, cash_out: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                    placeholder="Enter cash out amount"
                                    autoFocus
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Technical Grade (Optional)
                                    </label>
                                    <div className="flex gap-2">
                                        {['A', 'B', 'C'].map(grade => (
                                            <button
                                                key={grade}
                                                type="button"
                                                onClick={() => setEndSessionForm({...endSessionForm, technical_grade: endSessionForm.technical_grade === grade ? '' : grade})}
                                                className={`flex-1 px-3 py-2 rounded-lg border font-semibold transition-colors ${
                                                    endSessionForm.technical_grade === grade
                                                        ? 'bg-blue-100 dark:bg-blue-900 border-blue-500 dark:border-blue-400 text-blue-700 dark:text-blue-300'
                                                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-600'
                                                }`}
                                            >
                                                {grade}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Mental Grade (Optional)
                                    </label>
                                    <div className="flex gap-2">
                                        {['A', 'B', 'C'].map(grade => (
                                            <button
                                                key={grade}
                                                type="button"
                                                onClick={() => setEndSessionForm({...endSessionForm, mental_grade: endSessionForm.mental_grade === grade ? '' : grade})}
                                                className={`flex-1 px-3 py-2 rounded-lg border font-semibold transition-colors ${
                                                    endSessionForm.mental_grade === grade
                                                        ? 'bg-purple-100 dark:bg-purple-900 border-purple-500 dark:border-purple-400 text-purple-700 dark:text-purple-300'
                                                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-purple-300 dark:hover:border-purple-600'
                                                }`}
                                            >
                                                {grade}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowEndModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                                >
                                    End Session
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Checkpoint Modal - Update Current Stack */}
            {showCheckpointModal && (() => {
                const totalBuyIn = parseFloat(activeSession?.current_buy_in || 0);
                const stackValue = parseFloat(checkpointForm.stack_size) || 0;
                const calculatedPnL = checkpointForm.stack_size ? stackValue - totalBuyIn : 0;
                const isWinning = calculatedPnL >= 0;

                return (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {editingCheckpointId ? 'Edit Stack Update' : '📊 Update Current Stack'}
                                </h2>
                                <button onClick={handleCancelCheckpoint}>
                                    <XMarkIcon className="h-6 w-6 text-gray-500" />
                                </button>
                            </div>

                            {/* Current Buy-in Info */}
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
                                <div className="text-sm text-gray-500 dark:text-gray-400">Total Buy-in</div>
                                <div className="text-xl font-bold text-gray-900 dark:text-white">${totalBuyIn.toFixed(2)}</div>
                            </div>

                            <form onSubmit={(e) => {
                                // Auto-set P&L before submit
                                setCheckpointForm(prev => ({...prev, pnl: calculatedPnL.toString()}));
                                handleAddCheckpoint(e);
                            }} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Current Stack Size <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                                        <input
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={checkpointForm.stack_size}
                                            onChange={(e) => {
                                                const newStack = e.target.value;
                                                const newPnL = newStack ? (parseFloat(newStack) - totalBuyIn).toString() : '';
                                                setCheckpointForm({...checkpointForm, stack_size: newStack, pnl: newPnL});
                                            }}
                                            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pl-7 pr-3 py-3 text-xl font-bold text-gray-900 dark:text-white"
                                            required
                                            autoFocus
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                {/* Auto-calculated P&L Display */}
                                {checkpointForm.stack_size && (
                                    <div className={`rounded-lg p-4 ${
                                        isWinning
                                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                                            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                                    }`}>
                                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current P&L</div>
                                        <div className={`text-2xl font-bold ${
                                            isWinning ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                        }`}>
                                            {isWinning ? '+' : ''}${calculatedPnL.toFixed(2)}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Notes (optional)
                                    </label>
                                    <textarea
                                        value={checkpointForm.notes}
                                        onChange={(e) => setCheckpointForm({...checkpointForm, notes: e.target.value})}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                        rows="2"
                                        placeholder="Table conditions, mental state..."
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={handleCancelCheckpoint}
                                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!checkpointForm.stack_size}
                                        className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                    >
                                        {editingCheckpointId ? 'Save' : 'Update Stack'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                );
            })()}

            {/* Discipline Fold Modal */}
            {showBigFoldModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Discipline Fold</h2>
                            <button onClick={() => setShowBigFoldModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                            </button>
                        </div>
                        <form onSubmit={handleAddBigFold} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Hand Log
                                </label>
                                <textarea
                                    value={bigFoldForm.hand_log}
                                    onChange={(e) => setBigFoldForm({...bigFoldForm, hand_log: e.target.value})}
                                    required
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="4"
                                    placeholder="Hero: AK&#10;Board: K♥ 9♠ 3♦ 8♣ 2♥&#10;Pot: $450&#10;Villain shoved $200..."
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Include your hand, board texture, pot size, and players involved
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Call Amount <span className="text-gray-400">(optional)</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={bigFoldForm.call_amount}
                                    onChange={(e) => setBigFoldForm({...bigFoldForm, call_amount: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    placeholder="200.00"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Amount you would have needed to call (helps track money saved)
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Difficulty
                                </label>
                                <select
                                    value={bigFoldForm.difficulty}
                                    onChange={(e) => setBigFoldForm({...bigFoldForm, difficulty: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                >
                                    <option value="easy">Easy Fold</option>
                                    <option value="hard">Hard Fold</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description / Reasoning
                                </label>
                                <textarea
                                    value={bigFoldForm.description}
                                    onChange={(e) => setBigFoldForm({...bigFoldForm, description: e.target.value})}
                                    required
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="4"
                                    placeholder="Reasoning: Villain had been tight all night, this was his first 3-bet. Even though I had top pair top kicker, his range here is so strong...&#10;&#10;Confidence: High&#10;Outcome: He showed pocket kings"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Include your reasoning, confidence level, and outcome if known
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowBigFoldModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                                >
                                    Add Discipline Fold
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Curiosity Call Modal */}
            {showCuriosityCallModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Log Curiosity Call</h2>
                            <button onClick={() => setShowCuriosityCallModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                            </button>
                        </div>
                        <form onSubmit={handleAddCuriosityCall} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Hand Log
                                </label>
                                <textarea
                                    value={curiosityCallForm.hand_log}
                                    onChange={(e) => setCuriosityCallForm({...curiosityCallForm, hand_log: e.target.value})}
                                    required
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="4"
                                    placeholder="Hero: 78o&#10;Board: A♠ K♦ Q♥&#10;Pot: $150&#10;Villain bet $75..."
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Include your hand, board texture, pot size, and situation details
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Call Amount
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={curiosityCallForm.call_amount}
                                    onChange={(e) => setCuriosityCallForm({...curiosityCallForm, call_amount: e.target.value})}
                                    required
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    placeholder="75.00"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Amount you called (tracks money lost to curiosity)
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Severity
                                </label>
                                <select
                                    value={curiosityCallForm.severity}
                                    onChange={(e) => setCuriosityCallForm({...curiosityCallForm, severity: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                >
                                    <option value="pretty_bad">Pretty Bad Call</option>
                                    <option value="catastrophic">Catastrophic Call</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={curiosityCallForm.description}
                                    onChange={(e) => setCuriosityCallForm({...curiosityCallForm, description: e.target.value})}
                                    required
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="4"
                                    placeholder="Why did you make this call? What were you curious about? What happened?&#10;&#10;Example: Just wanted to see what he had. No equity vs his range. He showed the nuts."
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Explain what you were curious about and the outcome
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowCuriosityCallModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                                >
                                    Log Curiosity Call
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Checkpoint Confirmation Modal */}
            {deleteCheckpointId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Checkpoint</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Are you sure you want to delete this checkpoint? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteCheckpointId(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteCheckpoint}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Mistake Confirmation Modal */}
            {deleteMistakeId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Mistake</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Are you sure you want to delete this mistake? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteMistakeId(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteMistake}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat Panel / GPT Coach */}
            <ChatPanel
                sessionId={activeSession?.id}
                isOpen={showChatPanel}
                onClose={() => setShowChatPanel(false)}
                onHandsSaved={(count) => {
                    // Refresh session to show new hands
                    refreshSession();
                }}
            />
        </div>
    );
}

export default ActiveSessionPage;
